import { Component } from "react";
import { isChunkLoadError } from "../utils/lazyWithRetry";

/**
 * Error boundary that recovers from stale-deploy chunk failures by reloading
 * the page once, and renders a minimal fallback for any other render error
 * instead of leaving the user on a blank white screen.
 */
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // In dev, always log the real error + component stack so the generic
    // fallback below doesn't hide the actual cause.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error("[ChunkErrorBoundary] caught:", error, errorInfo);
    }
    if (isChunkLoadError(error)) {
      const reloadKey = "chunk-reload-attempted";
      if (window.sessionStorage.getItem(reloadKey) !== "true") {
        window.sessionStorage.setItem(reloadKey, "true");
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    window.sessionStorage.removeItem("chunk-reload-attempted");
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Chunk errors trigger an automatic reload in componentDidCatch; keep the
      // UI blank-but-stable for the instant before the reload kicks in.
      if (isChunkLoadError(this.state.error)) {
        return null;
      }
      return (
        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
            Something went wrong
          </h2>
          <p style={{ color: "#6b7280" }}>
            The page failed to load. Please try again.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre
              style={{
                maxWidth: "90vw",
                overflow: "auto",
                textAlign: "left",
                background: "#fef2f2",
                color: "#991b1b",
                padding: "1rem",
                borderRadius: "0.5rem",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
          <button
            onClick={this.handleReload}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.5rem",
              background: "#7c3aed",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
